import { useCallback, useEffect, useState } from 'react'
import { Check, Copy, Loader2, UserPlus, UserX } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { PAPEL_LABEL, type PapelMembro } from '@/lib/types'
import { criarConvite, desativarMembro, getMembros, revogarConvite, type MembroComPerfil } from '@/services/equipe'

const PAPEIS_CONVITE: Exclude<PapelMembro, 'medico_titular'>[] = [
  'secretaria_pessoal',
  'medico_colaborador',
  'secretaria_clinica',
]

export default function EquipePage() {
  const { current, papel } = useAuth()
  const [membros, setMembros] = useState<MembroComPerfil[]>([])
  const [loading, setLoading] = useState(true)
  const [erro, setErro] = useState<string | null>(null)

  const [showForm, setShowForm] = useState(false)
  const [email, setEmail] = useState('')
  const [papelConvite, setPapelConvite] = useState<Exclude<PapelMembro, 'medico_titular'>>('secretaria_pessoal')
  const [busy, setBusy] = useState(false)
  const [linkGerado, setLinkGerado] = useState<string | null>(null)
  const [copiado, setCopiado] = useState(false)

  const souTitular = papel === 'medico_titular'

  const carregar = useCallback(async () => {
    if (!current) return
    setLoading(true)
    setErro(null)
    try {
      setMembros(await getMembros(current.workspace.id))
    } catch {
      setErro('Não foi possível carregar a equipe.')
    } finally {
      setLoading(false)
    }
  }, [current])

  useEffect(() => { carregar() }, [carregar])

  async function convidar(e: React.FormEvent) {
    e.preventDefault()
    if (!current) return
    setBusy(true)
    setErro(null)
    try {
      const { token } = await criarConvite({
        workspaceId: current.workspace.id,
        email,
        papel: papelConvite,
      })
      setLinkGerado(`${window.location.origin}/convite/${token}`)
      setEmail('')
      await carregar()
    } catch {
      setErro('Não foi possível criar o convite.')
    } finally {
      setBusy(false)
    }
  }

  async function copiarLink() {
    if (!linkGerado) return
    await navigator.clipboard.writeText(linkGerado)
    setCopiado(true)
    setTimeout(() => setCopiado(false), 2000)
  }

  async function removerMembro(m: MembroComPerfil) {
    if (!current) return
    const nome = m.perfil?.nome ?? m.convite_email ?? 'este membro'
    if (!window.confirm(`Remover o acesso de ${nome}? A ação fica registrada na auditoria.`)) return
    try {
      if (m.convite_status === 'pendente') await revogarConvite(current.workspace.id, m.id)
      else await desativarMembro(current.workspace.id, m.id)
      await carregar()
    } catch {
      setErro('Não foi possível remover o membro.')
    }
  }

  return (
    <div>
      <header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h1 className="page-title">Equipe</h1>
          <p className="page-subtitle" style={{ margin: 0 }}>Secretárias e médicos com acesso à sua área.</p>
        </div>
        {souTitular && (
          <button className="btn-primary" onClick={() => { setShowForm(v => !v); setLinkGerado(null) }}>
            <UserPlus size={16} aria-hidden /> Convidar
          </button>
        )}
      </header>

      {souTitular && showForm && (
        <form onSubmit={convidar} className="card" style={{ padding: 18, marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
            <div>
              <label className="field-label" htmlFor="conv-email">E-mail *</label>
              <input id="conv-email" type="email" required className="field-input" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="field-label" htmlFor="conv-papel">Papel *</label>
              <select id="conv-papel" className="field-input" value={papelConvite} onChange={e => setPapelConvite(e.target.value as typeof papelConvite)}>
                {PAPEIS_CONVITE.map(p => (
                  <option key={p} value={p}>{PAPEL_LABEL[p]}</option>
                ))}
              </select>
            </div>
          </div>
          {papelConvite === 'secretaria_clinica' && (
            <div className="badge badge-warn">O vínculo com uma clínica específica é definido após o cadastro de clínicas (Etapa 2).</div>
          )}
          <div>
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy && <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />}
              Gerar link de convite
            </button>
          </div>
          {linkGerado && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <code style={{ fontSize: 12.5, background: 'var(--bg)', padding: '8px 10px', borderRadius: 8, wordBreak: 'break-all', flex: 1, minWidth: 220 }}>
                {linkGerado}
              </code>
              <button type="button" className="btn-secondary" onClick={copiarLink} style={{ minHeight: 38 }}>
                {copiado ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
                {copiado ? 'Copiado' : 'Copiar'}
              </button>
            </div>
          )}
        </form>
      )}

      {erro && <div className="field-error" role="alert" style={{ marginBottom: 12 }}>{erro}</div>}

      {loading ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
          <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {membros.map(m => (
            <div key={m.id} className="card" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14.5 }}>
                  {m.perfil?.nome ?? m.convite_email ?? '—'}
                </div>
                <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>
                  {PAPEL_LABEL[m.papel]}{m.perfil?.email ? ` · ${m.perfil.email}` : ''}
                </div>
              </div>
              {m.convite_status === 'pendente' && <span className="badge badge-warn">Convite pendente</span>}
              {!m.ativo && <span className="badge badge-muted">Inativo</span>}
              {souTitular && m.papel !== 'medico_titular' && m.ativo && (
                <button
                  className="btn-secondary"
                  style={{ minHeight: 36, padding: '6px 12px', fontSize: 13 }}
                  onClick={() => removerMembro(m)}
                  aria-label={`Remover ${m.perfil?.nome ?? m.convite_email ?? 'membro'}`}
                >
                  <UserX size={15} aria-hidden /> Remover
                </button>
              )}
            </div>
          ))}
          {membros.length === 0 && (
            <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
              Nenhum membro ainda.
            </div>
          )}
        </div>
      )}
    </div>
  )
}
